import React from 'react'
import { Button, Form, Icon, TextArea, Popup } from 'semantic-ui-react'
import { Field, useForm, useFormState } from 'react-final-form'
import { LINEAGE_OPTIONS, PRODUCT_SUBTYPE_OPTIONS, PRODUCT_TYPE_OPTIONS } from '../../../consts'
import { ProductKind } from '../../../types'
import { BrandSelect } from '../Form/BrandSelect'

export const Details = () => {
  const { values } = useFormState()
  const {
    change,
    mutators: { titleCase, appendAmountAndDosage, clearRootSubType },
  } = useForm()

  const subtypeOptions = (kindValue: string, subtypeValue: string) => {
    if (!kindValue) return []

    const opts = PRODUCT_SUBTYPE_OPTIONS[kindValue as ProductKind]

    if (!opts) return []

    // This is simply to make non-standard subcategories less confusing. We also show an error.
    if (!opts.map((o) => o.value).includes(subtypeValue)) {
      return [{ key: subtypeValue, text: subtypeValue, value: subtypeValue }, ...opts]
    }

    return opts
  }

  return (
    <>
      <Field
        name="name"
        formatOnBlur={true}
        format={(value: string | undefined) => (value ? value.trim() : '')}
      >
        {({ input, meta }) => (
          <Form.Field required error={!!(meta.touched && meta.error)}>
            <label>Name</label>
            <Form.Input
              error={meta.touched && meta.error}
              type="text"
              action
              {...input}
              value={input.value || ''}
            >
              <input />
              <Popup
                content="Titlecase"
                position="top center"
                trigger={
                  <Button
                    type="button"
                    icon
                    tabIndex="-1"
                    onClick={() => {
                      titleCase('name')
                    }}
                  >
                    <Icon name="magic" />
                  </Button>
                }
              />
              <Popup
                content="Append amount and dosage"
                position="top center"
                trigger={
                  <Button
                    type="button"
                    icon
                    tabIndex="-1"
                    onClick={() => {
                      appendAmountAndDosage('name')
                    }}
                  >
                    <Icon name="paste" />
                  </Button>
                }
              />
            </Form.Input>
          </Form.Field>
        )}
      </Field>

      <Field name="product_brand_id">
        {({ input }) => (
          <BrandSelect
            handleChange={(productBrandId?: string | number) => {
              input.onChange(productBrandId)
              change('brand', '')
            }}
            productBrandId={input.value}
            productBrand={values.brand}
          />
        )}
      </Field>

      <Field
        name="description"
        formatOnBlur={true}
        format={(value: string | undefined) => (value ? value.trim() : '')}
      >
        {({ input, meta }) => (
          <Form.Field error={meta.touched && meta.error} required>
            <label>Description</label>
            <TextArea
              {...input}
              rows="12"
              placeholder="Product Description"
              style={{ width: '100%' }}
            />
            {/* TextArea doesn't support an error key, manually added it here */}
            {meta.touched && meta.error && (
              <div className="ui pointing above prompt label" role="alert" aria-atomic="true">
                {meta.error}
              </div>
            )}
          </Form.Field>
        )}
      </Field>

      <Field name="category">
        {({ input, meta }) => (
          <>
            <Form.Select
              id="lineage"
              label="Lineage"
              clearable
              selectOnBlur={false}
              error={meta.error}
              value={input.value}
              onChange={(_, { value }) => input.onChange(value)}
              options={[...LINEAGE_OPTIONS]}
              data-testid="lineage-select"
            />
          </>
        )}
      </Field>

      <Field name="kind">
        {({ input, meta }) => (
          <Form.Select
            label="Product Type"
            clearable
            required
            selectOnBlur={false}
            error={meta.modified && meta.error}
            value={input.value}
            onChange={(_, { value }) => {
              clearRootSubType()
              input.onChange(value)
            }}
            options={PRODUCT_TYPE_OPTIONS.slice(1)}
            data-testid="product-type-select"
          />
        )}
      </Field>

      <Field name="root_subtype">
        {({ input, meta }) => (
          <Form.Select
            label="Product Subtype"
            clearable
            selectOnBlur={false}
            error={meta.error}
            value={input.value}
            onChange={(_, { value }) => {
              input.onChange(value)
            }}
            options={subtypeOptions(values?.kind, values?.root_subtype)}
            data-testid="product-subtype-select"
          />
        )}
      </Field>

      <Field
        name="kind_subtype"
        formatOnBlur={true}
        format={(value: string | undefined) => (value ? value.trim() : '')}
      >
        {({ input, meta }) => (
          <Form.Field error={meta.touched && meta.error}>
            <label>Brand Subtype</label>
            <Form.Input
              placeholder="string"
              error={meta.touched && meta.error}
              type="text"
              {...input}
              value={input.value || ''}
            />
          </Form.Field>
        )}
      </Field>
    </>
  )
}
